(() => {
  class EditionProductGallery extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.track = this.querySelector('.product-edition-gallery__track');
      this.thumbsContainer = this.querySelector('.product-edition-gallery__thumbs');
      this.empty = this.querySelector('[data-gallery-empty]');
      this.dialog = this.querySelector('[data-gallery-dialog]');
      this.dialogImage = this.querySelector('[data-gallery-dialog-image]');
      this.returnFocus = null;
      this.pointerStartX = null;
      this.visibleItems = [];
      this.activeIndex = 0;
      this.initialized = false;
      const signal = this.controller.signal;
      this.addEventListener('click', (event) => this.handleClick(event), { signal });
      this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
      this.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
      this.addEventListener('pointerup', (event) => this.handlePointerUp(event), { signal });
      this.track?.addEventListener('scroll', () => this.handleScroll(), { passive: true, signal });
      this.closest('[data-main-product]')?.addEventListener('product:variant-change', (event) => this.handleVariantChange(event), { signal });
      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), { signal });
      this.dialog?.addEventListener('close', () => this.returnFocus?.focus(), { signal });
      window.addEventListener('resize', () => this.show(this.activeIndex, false, false), { signal });
      this.setEdition(this.dataset.initialEdition || this.dataset.defaultEdition, true);
      this.initialized = true;
    }

    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.scrollFrame);
    }

    normalize(value) {
      return String(value || '').trim().toLowerCase();
    }

    itemsForEdition(edition) {
      const exact = this.items.filter((item) => this.normalize(item.dataset.edition) === this.normalize(edition));
      if (exact.length) return exact;
      return this.items.filter((item) => this.normalize(item.dataset.edition) === 'shared');
    }

    setEdition(edition, reset) {
      const normalized = this.normalize(edition);
      const editionChanged = normalized !== this.normalize(this.currentEdition);
      this.currentEdition = edition;
      this.visibleItems = this.itemsForEdition(edition);
      const visibleKeys = new Set(this.visibleItems.map((item) => item.dataset.galleryKey));
      this.items.forEach((item) => { item.hidden = !visibleKeys.has(item.dataset.galleryKey); });
      this.thumbs.forEach((thumb) => { thumb.hidden = !visibleKeys.has(thumb.dataset.galleryKey); });
      if (reset || editionChanged || this.activeIndex >= this.visibleItems.length) this.activeIndex = 0;
      this.empty.hidden = this.visibleItems.length > 0;
      cancelAnimationFrame(this.scrollFrame);
      this.show(this.activeIndex, false, false);
    }

    show(index, focusThumbnail = false, animate = true) {
      const total = this.visibleItems.length;
      if (!total) {
        this.items.forEach((item) => { item.hidden = true; });
        this.thumbs.forEach((thumb) => { thumb.hidden = true; thumb.setAttribute('aria-current', 'false'); });
        this.updateDialog();
        return;
      }
      this.activeIndex = Math.min(Math.max(index, 0), total - 1);
      this.track?.scrollTo({ left: this.activeIndex * this.track.clientWidth, behavior: this.initialized && animate ? 'smooth' : 'auto' });
      this.updateSelection(focusThumbnail);
    }

    updateSelection(focusThumbnail = false) {
      const activeItem = this.visibleItems[this.activeIndex];
      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.galleryKey === activeItem.dataset.galleryKey;
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active && focusThumbnail) thumb.focus();
        if (active && this.initialized && this.thumbsContainer) {
          const left = thumb.offsetLeft - this.thumbsContainer.offsetLeft - (this.thumbsContainer.clientWidth - thumb.clientWidth) / 2;
          this.thumbsContainer.scrollTo({ left, behavior: 'auto' });
        }
      });
      this.updateDialog();
    }

    handleScroll() {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        if (!this.track?.clientWidth || !this.visibleItems.length) return;
        const index = Math.min(Math.round(this.track.scrollLeft / this.track.clientWidth), this.visibleItems.length - 1);
        if (index === this.activeIndex) return;
        this.activeIndex = index;
        this.updateSelection();
      });
    }

    move(step, focusThumbnail = false) {
      if (this.visibleItems.length < 2) return;
      const nextIndex = Math.min(Math.max(this.activeIndex + step, 0), this.visibleItems.length - 1);
      this.show(nextIndex, focusThumbnail);
    }

    handleClick(event) {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) {
        const nextIndex = this.visibleItems.findIndex((item) => item.dataset.galleryKey === thumb.dataset.galleryKey);
        if (nextIndex >= 0) this.show(nextIndex, false, false);
        return;
      }
      const zoomButton = event.target.closest('[data-gallery-zoom-open]');
      if (zoomButton && this.dialog?.showModal) {
        this.returnFocus = zoomButton;
        this.dialog.showModal();
        this.updateDialog();
        this.dialog.querySelector('[data-gallery-dialog-close]')?.focus();
      }
      if (event.target.closest('[data-gallery-dialog-close]')) this.dialog?.close();
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1, !this.dialog?.open);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1, !this.dialog?.open);
      }
    }

    handlePointerDown(event) {
      if (!event.target.closest('[data-gallery-dialog]') || event.pointerType === 'mouse') return;
      this.pointerStartX = event.clientX;
    }

    handlePointerUp(event) {
      if (this.pointerStartX === null || event.pointerType === 'mouse') return;
      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      if (Math.abs(distance) < 42 || !this.dialog?.open) return;
      this.move(distance > 0 ? -1 : 1);
    }

    handleVariantChange(event) {
      const edition = event.detail?.edition;
      if (!edition) return;
      this.setEdition(edition, this.normalize(edition) !== this.normalize(this.currentEdition));
    }

    handleBlockSelect(event) {
      const block = event.target?.closest?.('[data-gallery-item], [data-gallery-guidance]');
      if (!block || !this.contains(block)) return;
      const edition = block.dataset.edition;
      if (edition && this.normalize(edition) !== 'shared') this.setEdition(edition, true);
      const item = this.items.find((candidate) => candidate.dataset.blockId === block.dataset.blockId);
      if (!item) return;
      const itemIndex = this.visibleItems.indexOf(item);
      if (itemIndex >= 0) this.show(itemIndex, false);
    }

    updateDialog() {
      if (!this.dialogImage || !this.dialog?.open) return;
      const item = this.visibleItems[this.activeIndex];
      const image = item?.querySelector('img');
      if (!image) {
        this.dialogImage.removeAttribute('src');
        this.dialogImage.alt = '';
        return;
      }
      this.dialogImage.src = item.dataset.zoomSrc || image.currentSrc || image.src;
      this.dialogImage.alt = image.alt;
    }
  }

  if (!customElements.get('edition-product-gallery')) customElements.define('edition-product-gallery', EditionProductGallery);
})();
